import React from 'react';
import { useSelector } from 'react-redux';
import { MenuSections } from '@scope/scope-one-ui/core/GeneralServicesMenu/GeneralServicesMenu.d';
import {
  getScopeOneUrl as SCI,
  getFundAnalysisUrl as SCOPEX,
  getAdminUrl as ADMIN,
} from '@scope/scope-one-common/utils/apiUrl';
import { UserRole, ModuleLicence, ShopPermission } from '@scope/scope-one-common/types/auth';
import Clipboard from '@scope/scope-one-common/assets/icons/clipboard.svg';
import Star from '@scope/scope-one-common/assets/icons/star.svg';
import Briefcase from '@scope/scope-one-common/assets/icons/briefcase.svg';
import BarChart from '@scope/scope-one-common/assets/icons/bar-chart-2.svg';
import Archive from '@scope/scope-one-common/assets/icons/archive.svg';
import Cast from '@scope/scope-one-common/assets/icons/cast.svg';
import Plus from '@scope/scope-one-common/assets/icons/plus.svg';
import Layout from '@scope/scope-one-common/assets/icons/layout.svg';
import List from '@scope/scope-one-common/assets/icons/list.svg';
import Key from '@scope/scope-one-common/assets/icons/key.svg';
import RotateCcw from '@scope/scope-one-common/assets/icons/rotate-ccw.svg';
import { getAuthData } from '../../../selectors/auth';

export const useGeneralServices = (): MenuSections => {
  const auth = useSelector(getAuthData);

  const hasRole = (role: UserRole) => auth.roles?.includes(role);

  const hasLicense = (licence: ModuleLicence) => auth.licences?.includes(licence);

  const hasShopPermit = (permission: ShopPermission) => auth.shopPermissions?.includes(permission);

  return {
    list: [
      {
        title: 'General services',
        canAccess: hasRole('SERVICE_CENTRE') || hasRole('ORGANISATION_ADMIN'),
        options: [
          {
            value: ADMIN('organisations'),
            label: 'Dashboard',
            icon: <Layout />,
          },
          {
            value: ADMIN('subscriptions'),
            label: 'Subscriptions',
            icon: <List />,
          },
          {
            value: ADMIN('request-history'),
            label: 'Request history',
            icon: <RotateCcw />,
          },
          {
            value: ADMIN('master-password'),
            label: 'Master password',
            icon: <Key />,
          },
        ],
      },
      {
        title: 'Subscription panel',
        canAccess: hasLicense('INVESTORHUB_MODULE') && hasShopPermit('SHOP_ADMIN'),
        options: [
          {
            value: SCI('subscriptions/panel'),
            label: 'View products',
            icon: <Layout />,
            canAccess: hasShopPermit('SHOP_ADMIN'),
          },
          {
            value: SCI('subscriptions/panel/create'),
            label: 'Create product',
            icon: <Plus />,
            canAccess: hasShopPermit('SHOP_ADMIN'),
          },
        ],
      },
      {
        title: 'Events panel',
        canAccess: hasLicense('INVESTORHUB_MODULE') && hasShopPermit('SHOP_ADMIN'),
        options: [
          {
            value: SCI('event-panel/view'),
            label: 'View events',
            icon: <Layout />,
            canAccess: hasShopPermit('SHOP_ADMIN'),
          },
          {
            value: SCI('event-panel/create'),
            label: 'Create event',
            icon: <Plus />,
            canAccess: hasShopPermit('SHOP_ADMIN'),
          },
        ],
      },
      {
        title: 'Services',
        canAccess: hasLicense('SCOPEEXPLORER_MODULE'),
        options: [
          { value: SCOPEX('conferences'), label: 'Conferences', icon: <Cast /> },
          { value: SCOPEX('aif-page'), label: 'OIF Studie 2020', icon: <Archive /> },
        ],
      },
      {
        title: 'Publications',
        canAccess: hasLicense('SCOPEEXPLORER_MODULE'),
        options: [
          { value: SCOPEX('news'), label: 'News', icon: <Clipboard /> },
          { value: SCOPEX('reports'), label: 'Reports', icon: <BarChart /> },
        ],
      },
      {
        title: 'Team ratings',
        canAccess: hasLicense('SCOPEEXPLORER_MODULE'),
        options: [
          { value: SCOPEX('asset-managers'), label: 'Asset Manager', icon: <Briefcase /> },
          { value: SCOPEX('premium'), label: 'Premium', icon: <Star /> },
        ],
      },
      {
        title: 'Admin',
        canAccess: hasLicense('ADMIN_MODULE') && hasRole('SERVICE_CENTRE'),
        options: [
          {
            value: SCI('admin-panel/rsr'),
            label: 'Restricted Subscription Ratings',
            icon: <Layout />,
          },
          {
            value: SCI('admin-panel/psr'),
            label: 'Private Subscription Ratings',
            icon: <Layout />,
          },
          {
            value: SCI('admin-panel/on-demand'),
            label: 'On-Demand Subscription Ratings',
            icon: <Layout />,
          },
        ],
      },
    ],
  };
};
